import fs from 'node:fs';
import { DailyLogRotator, appendRotatingLogSync } from '@lore/logging';
import * as Sentry from '@sentry/node';
import { getCurrentEnvironment } from './env.ts';
import { getLogFile, getStateDir } from './paths.ts';

type Level = 'info' | 'warn' | 'error';

type Fields = Record<string, unknown>;

let readyDir: string | null = null;
let stderrBroken = false;
const logRotator = new DailyLogRotator();

function ensureDir(): void {
  const stateDir = getStateDir();
  if (readyDir === stateDir) return;
  fs.mkdirSync(stateDir, { recursive: true });
  readyDir = stateDir;
}

function formatField(value: unknown): string {
  const s = typeof value === 'string' ? value : JSON.stringify(value);
  return /[\s"=]/.test(s) ? JSON.stringify(s) : s;
}

function format(level: Level, event: string, fields?: Fields): string {
  const head = `${new Date().toISOString()}  ${level}  ${event}`;
  if (!fields) return `${head}\n`;
  const tail = Object.entries(fields)
    .map(([k, v]) => `${k}=${formatField(v)}`)
    .join('  ');
  return tail ? `${head}  ${tail}\n` : `${head}\n`;
}

function write(level: Level, event: string, fields?: Fields): void {
  try {
    const line = format(level, event, fields);
    ensureDir();
    // CLI processes are short-lived and the entry point calls `process.exit`,
    // so a buffered WriteStream can lose its tail. appendFileSync keeps the
    // logger self-contained — no flush dance, no exit hook.
    appendRotatingLogSync(getLogFile(), line, logRotator);
    // In dev we tee to stderr so log lines show up while iterating against
    // the local stack. Prod stays quiet so installed users don't see noise.
    if (getCurrentEnvironment() === 'dev') {
      writeDevStderr(line);
    }
  } catch {
    // Logging must never change CLI command behavior.
  }
  forwardToSentry(level, event, fields);
}

function writeDevStderr(line: string): void {
  if (stderrBroken) return;
  try {
    fs.writeSync(process.stderr.fd, line);
  } catch {
    stderrBroken = true;
  }
}

// Sentry's Logs feature (enableLogs in Sentry.init) ships structured log
// records to Sentry alongside the local file log. The event name becomes the
// log message and the structured fields become attributes, preserving the
// shape engineers see in `~/.local/state/lore/log` when querying Sentry.
function forwardToSentry(level: Level, event: string, fields?: Fields): void {
  if (getCurrentEnvironment() === 'dev') return;

  try {
    Sentry.logger[level](event, sanitizeAttributes(fields));
  } catch {
    // Logging must never change CLI command behavior.
  }
}

// Sentry attributes must be primitives or arrays of primitives; anything else
// is dropped or stringified inconsistently. Coerce non-primitive fields to a
// JSON string so the original shape stays inspectable in Sentry.
function sanitizeAttributes(fields?: Fields): Record<string, string | number | boolean> | undefined {
  if (!fields) return undefined;
  const out: Record<string, string | number | boolean> = {};
  for (const [key, value] of Object.entries(fields)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
      out[key] = value;
      continue;
    }
    try {
      out[key] = JSON.stringify(value);
    } catch {
      out[key] = String(value);
    }
  }
  return out;
}

export const log = {
  info(event: string, fields?: Fields): void {
    write('info', event, fields);
  },
  warn(event: string, fields?: Fields): void {
    write('warn', event, fields);
  },
  error(event: string, fields?: Fields): void {
    write('error', event, fields);
  },
};
